import { HttpError } from 'react-admin';
import type { DataProvider, GetListParams } from 'react-admin';

import { requirePartner } from '@src/providers/dataProvider/shared';

import { fetchItemAdjustedPrices } from './fetchItemAdjustedPrices';
import type { YAdjustedRowWithId } from './types';

interface ItemAdjustedPricesDataProvider {
  getList: (
    resource: string,
    params: GetListParams,
  ) => Promise<{ data: YAdjustedRowWithId[], total: number }>;
  getOne: () => Promise<never>;
  getMany: () => Promise<never>;
  getManyReference: () => Promise<never>;
  create: () => Promise<never>;
  update: () => Promise<never>;
  updateMany: () => Promise<never>;
  delete: () => Promise<never>;
  deleteMany: () => Promise<never>;
}

/**
 * Data provider for itemAdjustedPrices resource.
 *
 * Read-only — only getList is implemented. Uses offset pagination (page/pageSize),
 * unlike pricingRules which uses cursor-based pagination (first/after).
 *
 * Y-adjusted rows have no server-side primary key (they are grouped aggregates
 * from CatalogItem). A synthetic id is generated from parentSku + skuSubcode +
 * color + row index for react-admin compatibility.
 */
const itemAdjustedPricesDataProviderImpl: ItemAdjustedPricesDataProvider = {
  getList: async (_resource, params) => {
    try {
      const partner = requirePartner();
      const { page = 1, perPage = 50 } = params.pagination || {};

      const result = await fetchItemAdjustedPrices({
        partnerId: partner.id,
        page,
        pageSize: perPage,
      });

      // React-admin requires an `id` field on every record.
      // Y-adjusted rows have no server-side ID — synthesize from the unique key.
      const data: YAdjustedRowWithId[] = result.rows.map((row, index) => ({
        ...row,
        id: `${row.parentSku ?? ''}-${row.skuSubcode ?? ''}-${row.color ?? ''}-${index}`,
      }));

      return {
        data,
        total: result.count,
      };
    } catch (error) {
      if (error instanceof HttpError) throw error;
      console.error('itemAdjustedPricesDataProvider.getList failed:', {
        error: error instanceof Error ? error.message : error,
      });
      const status = (error as { status?: number }).status || 500;
      throw new HttpError('Failed to load y-adjusted pricing.', status);
    }
  },

  getOne: async () => { throw new Error('getOne not implemented for itemAdjustedPrices resource'); },
  getMany: async () => { throw new Error('getMany not implemented for itemAdjustedPrices resource'); },
  getManyReference: async () => { throw new Error('getManyReference not implemented for itemAdjustedPrices resource'); },
  create: async () => { throw new Error('create not implemented for itemAdjustedPrices resource'); },
  update: async () => { throw new Error('update not implemented for itemAdjustedPrices resource'); },
  updateMany: async () => { throw new Error('updateMany not implemented for itemAdjustedPrices resource'); },
  delete: async () => { throw new Error('delete not implemented for itemAdjustedPrices resource'); },
  deleteMany: async () => { throw new Error('deleteMany not implemented for itemAdjustedPrices resource'); },
};

// Cast to DataProvider for react-admin compatibility.
// React-admin's DataProvider methods are generic, which prevents type-safe
// single-resource providers. We define proper types above and cast here.
export const itemAdjustedPricesDataProvider = itemAdjustedPricesDataProviderImpl as unknown as DataProvider;
export default itemAdjustedPricesDataProvider;
